import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ListPageHeader from '../common/ListPageHeader.js';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from "../common/Loader.js"
import Alert from '../common/Alert.js';
import companySubscriptionService from '../../services/CompanySubscriptionService.js';
import { CreditCard, Plus, CheckCircle2, XCircle, Clock, Calendar, ChevronDown, ChevronRight } from "lucide-react";
import ListFilterPanel from '../common/ListFilterPanel.js';
import SearchSubmitInput from '../common/SearchSubmitInput.js';
import Button from '../common/Button.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import RenewSubscriptionModal from './RenewSubscriptionModal.js';
import ListPagination from '../common/ListPagination.js';
import usePaginatedListFetch from '../../utils/usePaginatedListFetch.js';
import { paginateArray } from '../../utils/clientPaginate.js';
import { useTranslation } from 'react-i18next';
import { listIdLinkClass, listIdLinkMobileClass } from '../common/uiClasses.js';


const CompanySubscriptionList = () => {
  const { t } = useTranslation();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 10;
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [renewTarget, setRenewTarget] = useState(null);
  const [expandedCompanies, setExpandedCompanies] = useState(new Set());
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const subscriptionIdByDbId = useMemo(() => {
    const map = {};
    subscriptions.forEach((sub) => {
      map[sub.id] = sub.subscription_id;
    });
    return map;
  }, [subscriptions]);

  const companyGroups = useMemo(() => {
    const byCompany = {};
    subscriptions.forEach((sub) => {
      const key = sub.company_id ?? sub.company_identifier ?? String(sub.id);
      if (!byCompany[key]) byCompany[key] = [];
      byCompany[key].push(sub);
    });

    return Object.entries(byCompany)
      .map(([key, subs]) => {
        const sorted = [...subs].sort((a, b) => {
          if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
          return new Date(b.from_date) - new Date(a.from_date);
        });
        return {
          key,
          current: sorted[0],
          history: sorted.slice(1),
          totalCount: sorted.length,
        };
      })
      .sort((a, b) =>
        (a.current.company_name || '').localeCompare(b.current.company_name || '')
      );
  }, [subscriptions]);

  const pagedCompanyGroups = useMemo(() => {
    const { items, totalPages } = paginateArray(companyGroups, currentPage, perPage);
    return { items, totalPages };
  }, [companyGroups, currentPage, perPage]);

  useEffect(() => {
    if (currentPage > pagedCompanyGroups.totalPages) {
      setCurrentPage(pagedCompanyGroups.totalPages);
    }
  }, [currentPage, pagedCompanyGroups.totalPages]);

  const fetchSubscriptions = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;
      if (periodFilter) params.period_type = periodFilter;

      const response = await companySubscriptionService.getAll(params);
      setSubscriptions(response.data || []);
    } catch (err) {
      console.error('Error fetching subscriptions:', err);
      Alert.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter, periodFilter]);

  usePaginatedListFetch({
    fetchPage: fetchSubscriptions,
    setCurrentPage,
    resetPageDeps: [searchTerm, statusFilter, periodFilter],
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDelete = async (subscriptionId, subscriptionName) => {
    const confirmMessage = `Are you sure you want to delete subscription "${subscriptionName}"? This action cannot be undone.`;
    
    const confirmed = await Alert.confirm(confirmMessage, 'Delete Subscription');
    
    if (confirmed) {
      try {
        await companySubscriptionService.delete(subscriptionId);
        Alert.success('Subscription deleted successfully!');
        fetchSubscriptions();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete subscription');
      }
    }
  };

  const handleToggleStatus = async (subscriptionId, subscriptionName, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    const confirmMessage = `Are you sure you want to ${newStatus === 'active' ? 'activate' : 'deactivate'} subscription "${subscriptionName}"?`;
    
    const confirmed = await Alert.confirm(confirmMessage, 'Toggle Status');
    
    if (confirmed) {
      try {
        if (newStatus === 'active') {
          await companySubscriptionService.activate(subscriptionId);
        } else {
          await companySubscriptionService.delete(subscriptionId);
        }
        Alert.success(`Subscription ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully!`);
        fetchSubscriptions();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to toggle subscription status');
      }
    }
  };

  const toggleDropdown = (subscriptionId) => {
    setActiveDropdown(activeDropdown === subscriptionId ? null : subscriptionId);
  };

  const toggleCompanyExpand = (companyKey) => {
    setExpandedCompanies((prev) => {
      const next = new Set(prev);
      if (next.has(companyKey)) {
        next.delete(companyKey);
      } else {
        next.add(companyKey);
      }
      return next;
    });
  };

  const getRenewalLabel = (subscription) => {
    if (!subscription.is_renewal) return null;
    const previousId = subscription.previous_subscription_id
      ? subscriptionIdByDbId[subscription.previous_subscription_id]
      : null;
    return previousId ? `Renewal of ${previousId}` : 'Renewal';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const getStatusBadge = (status, isActive, isExpired) => {
    if (isActive) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
          <CheckCircle2 className="w-3 h-3" />
          Active
        </span>
      );
    } else if (isExpired) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
          <XCircle className="w-3 h-3" />
          Expired
        </span>
      );
    } else if (status?.toLowerCase() === 'inactive') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
          <XCircle className="w-3 h-3" />
          Inactive
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
          <Clock className="w-3 h-3" />
          Upcoming
        </span>
      );
    }
  };

  const getPeriodBadge = (periodType, customYears) => {
    const displayName = companySubscriptionService.getPeriodDisplayName(periodType, customYears);
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-xl text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
        <Calendar className="w-3 h-3" />
        {displayName}
      </span>
    );
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setPeriodFilter('');
  };

  const hasActiveFilters = searchTerm || statusFilter || periodFilter;

  const renderSubscriptionRow = (subscription, {
    isHistory = false,
    showCompany = true,
    groupKey = null,
    historyCount = 0,
    isExpanded = false,
  } = {}) => (
    <tr
      key={subscription.id}
      className={`hover:bg-gray-50 ${isHistory ? 'bg-gray-50/80 border-l-4 border-l-orange-200' : ''}`}
    >
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="flex items-center">
          {!isHistory && historyCount > 0 && groupKey && (
            <button
              type="button"
              onClick={() => toggleCompanyExpand(groupKey)}
              className="mr-2 p-0.5 rounded hover:bg-gray-200 text-gray-500"
              title={isExpanded ? 'Hide subscription history' : 'Show subscription history'}
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>
          )}
          {isHistory && <span className="w-6 mr-2" />}
          <div className="flex-shrink-0 h-8 w-8 bg-orange-100 rounded-full flex items-center justify-center">
            <CreditCard className={`h-4 w-4 ${isHistory ? 'text-orange-400' : 'text-orange-600'}`} />
          </div>
          <div className="ml-3">
            <div
              className={listIdLinkClass}
              onClick={() => navigate(`/company-subscriptions/view/${subscription.id}`)}
            >
              {subscription.subscription_id}
            </div>
            {getRenewalLabel(subscription) && (
              <div className="text-xs text-blue-600 font-medium mt-0.5">
                {getRenewalLabel(subscription)}
              </div>
            )}
            {isHistory && (
              <div className="text-xs text-gray-400 mt-0.5">Previous period</div>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        {showCompany ? (
          <>
            <div className="text-sm text-gray-900">{subscription.company_name || 'N/A'}</div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-gray-500">{subscription.company_identifier || ''}</span>
              {historyCount > 0 && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">
                  {historyCount + 1} subscriptions
                </span>
              )}
            </div>
          </>
        ) : (
          <span className="text-xs text-gray-400 italic">Same company</span>
        )}
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        {getPeriodBadge(subscription.period_type, subscription.custom_years)}
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="text-sm text-gray-900">{formatDate(subscription.from_date)}</div>
        <div className="text-xs text-gray-500">to {formatDate(subscription.to_date)}</div>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="text-sm font-semibold text-gray-900">
          {formatCurrency(subscription.subscription_amount)}
        </div>
        <div className="text-xs text-gray-500">
          Paid: {formatCurrency(subscription.total_paid)}
        </div>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        {getStatusBadge(subscription.status, subscription.is_active, subscription.is_expired)}
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="text-sm text-gray-900">
          {subscription.is_expired ? (
            <span className="text-red-600 font-medium">Expired</span>
          ) : subscription.is_active ? (
            <span className="text-green-600 font-medium">{subscription.days_remaining} days</span>
          ) : (
            <span className="text-gray-500">-</span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
        <RowActionsMenu
          isOpen={activeDropdown === subscription.id}
          onToggle={() => toggleDropdown(subscription.id)}
          onClose={() => setActiveDropdown(null)}
          menuRef={dropdownRef}
          onView={() => navigate(`/company-subscriptions/view/${subscription.id}`)}
          onEdit={() => navigate(`/company-subscriptions/edit/${subscription.id}`)}
          onDelete={() => handleDelete(subscription.id, subscription.subscription_id)}
          extraItems={[
            {
              label: 'Renew',
              icon: '🔄',
              variant: 'primary',
              onClick: () => setRenewTarget(subscription),
            },
          ]}
          className="relative inline-block"
        />
      </td>
    </tr>
  );

  const renderSubscriptionCard = (subscription, {
    isHistory = false,
    showCompany = true,
    groupKey = null,
    historyCount = 0,
    isExpanded = false,
  } = {}) => (
    <div
      key={subscription.id}
      className={`rounded-xl shadow-sm p-4 ${
        isHistory
          ? 'bg-gray-50/80 border border-gray-200 border-l-4 border-l-orange-200 ml-3'
          : 'bg-white'
      }`}
    >
      <div className="flex items-start gap-2 mb-3">
        {!isHistory && historyCount > 0 && groupKey && (
          <button
            type="button"
            onClick={() => toggleCompanyExpand(groupKey)}
            className="p-0.5 rounded hover:bg-gray-200 text-gray-500 shrink-0 mt-1"
            title={isExpanded ? 'Hide subscription history' : 'Show subscription history'}
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
        )}
        <div className="p-2 bg-orange-100 rounded-xl shrink-0">
          <CreditCard className={`w-4 h-4 ${isHistory ? 'text-orange-400' : 'text-orange-600'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h3
            className={`${listIdLinkMobileClass} truncate`}
            onClick={() => navigate(`/company-subscriptions/view/${subscription.id}`)}
          >
            {subscription.subscription_id}
          </h3>
          {showCompany && (
            <p className="text-xs text-gray-600 truncate mt-0.5">
              {subscription.company_name || 'N/A'}
            </p>
          )}
          {getRenewalLabel(subscription) && (
            <p className="text-xs text-blue-600 font-medium mt-0.5">{getRenewalLabel(subscription)}</p>
          )}
          {isHistory && <p className="text-xs text-gray-400 mt-0.5">Previous period</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {getStatusBadge(subscription.status, subscription.is_active, subscription.is_expired)}
          <RowActionsMenu
            isOpen={activeDropdown === subscription.id}
            onToggle={() => toggleDropdown(subscription.id)}
            onClose={() => setActiveDropdown(null)}
            menuRef={dropdownRef}
            onView={() => navigate(`/company-subscriptions/view/${subscription.id}`)}
            onEdit={() => navigate(`/company-subscriptions/edit/${subscription.id}`)}
            onDelete={() => handleDelete(subscription.id, subscription.subscription_id)}
            extraItems={[
              {
                label: 'Renew',
                icon: '🔄',
                variant: 'primary',
                onClick: () => setRenewTarget(subscription),
              },
            ]}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <span className="text-gray-500">Period:</span>
          <p className="text-gray-900 font-medium mt-0.5">
            {companySubscriptionService.getPeriodDisplayName(
              subscription.period_type,
              subscription.custom_years
            )}
          </p>
        </div>
        <div>
          <span className="text-gray-500">Date Range:</span>
          <p className="text-gray-900 font-medium mt-0.5 text-[11px]">
            {formatDate(subscription.from_date)} – {formatDate(subscription.to_date)}
          </p>
        </div>
        <div>
          <span className="text-gray-500">Amount:</span>
          <p className="text-gray-900 font-semibold mt-0.5">
            {formatCurrency(subscription.subscription_amount)}
          </p>
        </div>
        <div>
          <span className="text-gray-500">Paid:</span>
          <p className="text-green-600 font-semibold mt-0.5">
            {formatCurrency(subscription.total_paid)}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <DashboardLayout>
      {loading && currentPage === 1 ? (
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading subscriptions..." />
        </div>
      ) : (
        <>
              <ListPageHeader
                title={t('pages.companySubscription.title')}
                icon={CreditCard}
                titleClassName="text-primary"
                iconClassName="w-6 h-6 sm:w-7 sm:h-7 text-primary shrink-0"
                className="mb-4 sm:mb-6"
              >
                <Button mobileIconOnly variant="primary" icon={Plus} onClick={() => navigate('/company-subscriptions/add')}>
                  Add Subscription
                </Button>
              </ListPageHeader>

              <ListFilterPanel hasActiveFilters={hasActiveFilters} onClearFilters={clearAllFilters}>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('common.search')}</label>
                  <SearchSubmitInput
                    value={searchTerm}
                    onSearch={setSearchTerm}
                    placeholder="Search by subscription ID, company..."
                    inputClassName="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                  >
                    <option value="">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Period Type</label>
                  <select
                    value={periodFilter}
                    onChange={(e) => setPeriodFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                  >
                    <option value="">All Periods</option>
                    <option value="seven_days">7 Days</option>
                    <option value="one_month">1 Month</option>
                    <option value="three_months">3 Months</option>
                    <option value="six_months">6 Months</option>
                    <option value="twelve_months">12 Months</option>
                    <option value="custom_years">Custom Years</option>
                  </select>
                </div>
              </ListFilterPanel>

              {/* Subscriptions List */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                {subscriptions.length === 0 ? (
                  <div className="text-center py-12">
                    <CreditCard className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No subscriptions found</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {hasActiveFilters ? 'Try adjusting your filters' : 'Get started by creating a new subscription'}
                    </p>
                    {!hasActiveFilters && (
                      <div className="mt-6">
                        <Button variant="primary" icon={Plus} onClick={() => navigate('/company-subscriptions/add')}>
                          Add Subscription
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {/* Mobile card view */}
                    <div className="md:hidden p-3 space-y-3">
                      {pagedCompanyGroups.items.map((group) => {
                        const isExpanded = expandedCompanies.has(group.key);
                        const historyCount = group.history.length;

                        return (
                          <React.Fragment key={group.key}>
                            {renderSubscriptionCard(group.current, {
                              groupKey: group.key,
                              historyCount,
                              isExpanded,
                            })}
                            {isExpanded &&
                              group.history.map((subscription) =>
                                renderSubscriptionCard(subscription, {
                                  isHistory: true,
                                  showCompany: false,
                                })
                              )}
                          </React.Fragment>
                        );
                      })}
                    </div>

                    {/* Tablet / desktop table */}
                    <div className="hidden md:block">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Subscription ID
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Company
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Period
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date Range
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Amount
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Days Left
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {pagedCompanyGroups.items.map((group) => {
                          const isExpanded = expandedCompanies.has(group.key);
                          const historyCount = group.history.length;

                          return (
                            <React.Fragment key={group.key}>
                              {renderSubscriptionRow(group.current, {
                                groupKey: group.key,
                                historyCount,
                                isExpanded,
                              })}
                              {isExpanded &&
                                group.history.map((subscription) =>
                                  renderSubscriptionRow(subscription, {
                                    isHistory: true,
                                    showCompany: false,
                                  })
                                )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                    </div>
                  </>
                )}
              </div>

              {!loading && companyGroups.length > 0 && (
                <ListPagination
                  currentPage={currentPage}
                  totalPages={pagedCompanyGroups.totalPages}
                  itemCount={pagedCompanyGroups.items.length}
                  itemLabel="company"
                  onPrevious={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  onNext={() => setCurrentPage((p) => Math.min(pagedCompanyGroups.totalPages, p + 1))}
                />
              )}

              {/* Summary Cards */}
              {subscriptions.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Total Subscriptions</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{subscriptions.length}</p>
                      </div>
                      <div className="p-3 bg-blue-100 rounded-full">
                        <CreditCard className="w-6 h-6 text-blue-600" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Active Subscriptions</p>
                        <p className="text-2xl font-bold text-green-600 mt-1">
                          {subscriptions.filter(s => s.is_active).length}
                        </p>
                      </div>
                      <div className="p-3 bg-green-100 rounded-full">
                        <CheckCircle2 className="w-6 h-6 text-green-600" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Expired Subscriptions</p>
                        <p className="text-2xl font-bold text-red-600 mt-1">
                          {subscriptions.filter(s => s.is_expired).length}
                        </p>
                      </div>
                      <div className="p-3 bg-red-100 rounded-full">
                        <XCircle className="w-6 h-6 text-red-600" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Total Revenue</p>
                        <p className="text-2xl font-bold text-orange-600 mt-1">
                          {formatCurrency(subscriptions.reduce((sum, s) => sum + (s.subscription_amount || 0), 0))}
                        </p>
                      </div>
                      <div className="p-3 bg-orange-100 rounded-full">
                        <Calendar className="w-6 h-6 text-orange-600" />
                      </div>
                    </div>
                  </div>
                </div>
              )}
        </>
      )}
      {renewTarget && (
        <RenewSubscriptionModal
          subscription={renewTarget}
          onClose={() => setRenewTarget(null)}
          onSuccess={() => {
            setRenewTarget(null);
            fetchSubscriptions();
          }}
        />
      )}
    </DashboardLayout>
  );
};

export default CompanySubscriptionList;